import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
    Box,
    Button,
    Typography,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    CardActions,
    Avatar,
    Tooltip,
    Autocomplete,
    InputAdornment,
    Tabs,
    Tab
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Archive as ArchiveIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Campaign as CampaignIcon,
    Image as ImageIcon,
    Videocam as VideoIcon,
    TextFields as TextIcon,
    ViewCarousel as CarouselIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Ad status options
const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'paused', label: 'Paused', color: 'warning' },
    { value: 'archived', label: 'Archived', color: 'error' }
];

// Ad type options
const adTypeOptions = [
    { value: 'banner', label: 'Banner', icon: <ImageIcon /> },
    { value: 'native', label: 'Native', icon: <TextIcon /> },
    { value: 'video', label: 'Video', icon: <VideoIcon /> },
    { value: 'audio', label: 'Audio', icon: <VideoIcon /> },
    { value: 'rich_media', label: 'Rich Media', icon: <ImageIcon /> },
    { value: 'carousel', label: 'Carousel', icon: <CarouselIcon /> },
    { value: 'story', label: 'Story', icon: <ImageIcon /> },
    { value: 'search_text', label: 'Search Text', icon: <TextIcon /> },
    { value: 'display_image', label: 'Display Image', icon: <ImageIcon /> }
];

// Ad format options by type
const formatOptions = {
    banner: ['728x90', '300x250', '300x600', '320x50', '468x60', '970x250'],
    native: ['fluid', '1200x627', '1200x628'],
    video: ['1920x1080', '1280x720', '854x480', '640x360'],
    audio: ['audio', '300x250'],
    rich_media: ['300x250', '728x90', '160x600'],
    carousel: ['1200x628', '1080x1080'],
    story: ['1080x1920', '1080x1350'],
    search_text: ['text'],
    display_image: ['300x250', '728x90', '160x600']
};

// Call to action options
const ctaOptions = [
    'Learn More', 'Shop Now', 'Sign Up', 'Download', 'Book Now',
    'Get Quote', 'Contact Us', 'Subscribe', 'Buy Now', 'Try Free',
    'Get Started', 'View More', 'Watch Now', 'Listen Now', 'Apply Now'
];

const AdsPage = () => {
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Table states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Filter states
    const [filters, setFilters] = useState({
        status: '',
        ad_type: '',
        campaign_id: '',
        search: '',
        startDate: null,
        endDate: null
    });

    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [selectedAdForAction, setSelectedAdForAction] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        campaign_id: '',
        name: '',
        ad_type: 'banner',
        status: 'draft',
        format: '728x90',
        creative_url: '',
        thumbnail_url: '',
        destination_url: '',
        headline: '',
        description: '',
        call_to_action: 'Learn More',
        display_url: ''
    });

    const [tabValue, setTabValue] = useState(0);

    // Fetch ads and campaigns
    useEffect(() => {
        fetchAds();
        fetchCampaigns();
    }, [page, rowsPerPage, filters]);

    const fetchAds = async () => {
        try {
            setLoading(true);

            // Get user session
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/login');
                return;
            }

            let query = supabase
                .from('ads')
                .select('*, campaigns(name)', { count: 'exact' })
                .eq('user_id', session.user.id)
                .order('created_at', { ascending: false });

            // Apply filters
            if (filters.status) {
                query = query.eq('status', filters.status);
            }
            if (filters.ad_type) {
                query = query.eq('ad_type', filters.ad_type);
            }
            if (filters.campaign_id) {
                query = query.eq('campaign_id', filters.campaign_id);
            }
            if (filters.search) {
                query = query.ilike('name', `%${filters.search}%`);
            }
            if (filters.startDate) {
                query = query.gte('created_at', filters.startDate.toISOString());
            }
            if (filters.endDate) {
                query = query.lte('created_at', filters.endDate.toISOString());
            }

            // Apply pagination
            const from = page * rowsPerPage;
            const to = from + rowsPerPage - 1;
            query = query.range(from, to);

            const { data, error, count } = await query;

            if (error) throw error;

            setAds(data || []);
            setTotalCount(count || 0);
        } catch (err) {
            console.error('Error fetching ads:', err);
            setError('Failed to load ads. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaigns = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            const { data, error } = await supabase
                .from('campaigns')
                .select('id, name, status')
                .eq('user_id', session.user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;

            setCampaigns(data || []);
        } catch (err) {
            console.error('Error fetching campaigns:', err);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const handleResetFilters = () => {
        setFilters({
            status: '',
            ad_type: '',
            campaign_id: '',
            search: '',
            startDate: null,
            endDate: null
        });
        setPage(0);
    };

    const handleActionMenuOpen = (event, ad) => {
        setActionMenuAnchor(event.currentTarget);
        setSelectedAdForAction(ad);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchor(null);
        setSelectedAdForAction(null);
    };

    const handleStatusChange = async (adId, newStatus) => {
        try {
            const { error } = await supabase
                .from('ads')
                .update({ status: newStatus })
                .eq('id', adId);

            if (error) throw error;

            setSuccess(`Ad status updated to ${newStatus}`);
            fetchAds();
            handleActionMenuClose();
        } catch (err) {
            console.error('Error updating ad status:', err);
            setError('Failed to update ad status.');
        }
    };

    const handleDeleteAd = async () => {
        try {
            const { error } = await supabase
                .from('ads')
                .delete()
                .eq('id', selectedAdForAction?.id);

            if (error) throw error;

            setSuccess('Ad deleted successfully');
            fetchAds();
            setDeleteDialogOpen(false);
            handleActionMenuClose();
        } catch (err) {
            console.error('Error deleting ad:', err);
            setError('Failed to delete ad.');
        }
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Update format options when ad_type changes
        if (field === 'ad_type' && formatOptions[value]) {
            setFormData(prev => ({
                ...prev,
                ad_type: value,
                format: formatOptions[value][0] || ''
            }));
        }
    };

    const handleCreateAd = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                setError('You must be logged in to create an ad');
                return;
            }

            // Validate required fields
            if (!formData.name || !formData.campaign_id || !formData.destination_url) {
                setError('Please fill in all required fields');
                return;
            }

            const adData = {
                ...formData,
                user_id: session.user.id,
                impressions: 0,
                clicks: 0,
                ctr: 0,
                conversions: 0
            };

            const { data, error } = await supabase
                .from('ads')
                .insert(adData)
                .select()
                .single();

            if (error) throw error;

            setSuccess('Ad created successfully!');
            setDialogOpen(false);
            resetForm();
            fetchAds();

            // Navigate to new ad detail if needed
            // navigate(`/ads/${data.id}`);
        } catch (err) {
            console.error('Error creating ad:', err);
            setError(err.message || 'Failed to create ad.');
        }
    };

    const resetForm = () => {
        setFormData({
            campaign_id: '',
            name: '',
            ad_type: 'banner',
            status: 'draft',
            format: '728x90',
            creative_url: '',
            thumbnail_url: '',
            destination_url: '',
            headline: '',
            description: '',
            call_to_action: 'Learn More',
            display_url: ''
        });
    };

    const handleEditAd = (ad) => {
        setSelectedAd(ad);
        setFormData({
            campaign_id: ad.campaign_id,
            name: ad.name,
            ad_type: ad.ad_type,
            status: ad.status,
            format: ad.format,
            creative_url: ad.creative_url || '',
            thumbnail_url: ad.thumbnail_url || '',
            destination_url: ad.destination_url || '',
            headline: ad.headline || '',
            description: ad.description || '',
            call_to_action: ad.call_to_action || 'Learn More',
            display_url: ad.display_url || ''
        });
        setDialogOpen(true);
    };

    const handleUpdateAd = async () => {
        try {
            const { error } = await supabase
                .from('ads')
                .update(formData)
                .eq('id', selectedAd.id);

            if (error) throw error;

            setSuccess('Ad updated successfully!');
            setDialogOpen(false);
            setSelectedAd(null);
            resetForm();
            fetchAds();
        } catch (err) {
            console.error('Error updating ad:', err);
            setError('Failed to update ad.');
        }
    };

    const handleViewAd = (adId) => {
        navigate(`/ads/${adId}`);
    };

    const getStatusColor = (status) => {
        const option = statusOptions.find(opt => opt.value === status);
        return option ? option.color : 'default';
    };

    const getAdTypeIcon = (type) => {
        const option = adTypeOptions.find(opt => opt.value === type);
        return option ? option.icon : <ImageIcon />;
    };

    const formatCTR = (ctr) => {
        return `${ctr}%`;
    };

    const renderAdPreview = () => {
        const { ad_type, creative_url, thumbnail_url, headline, description, call_to_action, display_url } = formData;

        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Ad Preview
                    </Typography>

                    {ad_type === 'banner' && (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, textAlign: 'center' }}>
                            {creative_url ? (
                                <img
                                    src={creative_url}
                                    alt="Ad preview"
                                    style={{ maxWidth: '100%', maxHeight: 200 }}
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            ) : (
                                <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                                    <Typography color="text.secondary">No creative uploaded</Typography>
                                </Box>
                            )}
                            {headline && <Typography variant="subtitle1" sx={{ mt: 1 }}>{headline}</Typography>}
                            {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
                            {call_to_action && (
                                <Button variant="contained" size="small" sx={{ mt: 1 }}>
                                    {call_to_action}
                                </Button>
                            )}
                            {display_url && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {display_url}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {ad_type === 'search_text' && (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f8f9fa' }}>
                            <Typography variant="body2" color="primary" gutterBottom>
                                {display_url || 'example.com'} › {headline?.toLowerCase()?.replace(/\s+/g, '-')}
                            </Typography>
                            <Typography variant="h6" color="primary" gutterBottom>
                                {headline}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {description}
                            </Typography>
                            {call_to_action && (
                                <Chip label={call_to_action} size="small" color="primary" variant="outlined" />
                            )}
                        </Box>
                    )}

                    {(ad_type === 'video' || ad_type === 'audio') && (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, textAlign: 'center' }}>
                            {thumbnail_url ? (
                                <Box sx={{ position: 'relative' }}>
                                    <img
                                        src={thumbnail_url}
                                        alt="Video thumbnail"
                                        style={{ maxWidth: '100%', maxHeight: 150 }}
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <PlayIcon sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: 40,
                                        color: 'white',
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        borderRadius: '50%',
                                        p: 1
                                    }} />
                                </Box>
                            ) : (
                                <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                                    <Typography color="text.secondary">Video thumbnail preview</Typography>
                                </Box>
                            )}
                            {headline && <Typography variant="subtitle1" sx={{ mt: 1 }}>{headline}</Typography>}
                        </Box>
                    )}

                    {ad_type === 'native' && (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <Box sx={{ width: '100%', height: 80, bgcolor: '#f5f5f5', borderRadius: 1 }} />
                                </Grid>
                                <Grid item xs={9}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Sponsored
                                    </Typography>
                                    <Typography variant="subtitle1">{headline}</Typography>
                                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {display_url || 'example.com'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Ads Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/panel/NewAdWizardPage')}
                        sx={{ mb: 3 }}
                    >
                        Create New Ad
                    </Button>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search Ads"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {statusOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Ad Type</InputLabel>
                                <Select
                                    value={filters.ad_type}
                                    onChange={(e) => handleFilterChange('ad_type', e.target.value)}
                                    label="Ad Type"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {adTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Campaign</InputLabel>
                                <Select
                                    value={filters.campaign_id}
                                    onChange={(e) => handleFilterChange('campaign_id', e.target.value)}
                                    label="Campaign"
                                >
                                    <MenuItem value="">All Campaigns</MenuItem>
                                    {campaigns.map((campaign) => (
                                        <MenuItem key={campaign.id} value={campaign.id}>
                                            {campaign.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleResetFilters}
                                size="small"
                            >
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Stats Summary */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                    Total Ads
                                </Typography>
                                <Typography variant="h4">
                                    {totalCount}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                    Active Ads
                                </Typography>
                                <Typography variant="h4">
                                    {ads.filter(ad => ad.status === 'active').length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                    Total Impressions
                                </Typography>
                                <Typography variant="h4">
                                    {ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                    Total Clicks
                                </Typography>
                                <Typography variant="h4">
                                    {ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Ads Table */}
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ad Name</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Campaign</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Impressions</TableCell>
                                            <TableCell>Clicks</TableCell>
                                            <TableCell>CTR</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ads.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                    <Typography color="text.secondary">
                                                        No ads found. Create your first ad!
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            ads.map((ad) => (
                                                <TableRow key={ad.id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                                {getAdTypeIcon(ad.ad_type)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body1">{ad.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {ad.format}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={ad.ad_type}
                                                            size="small"
                                                            icon={getAdTypeIcon(ad.ad_type)}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <CampaignIcon sx={{ mr: 1, fontSize: 16, color: 'action.active' }} />
                                                            <Typography variant="body2">
                                                                {ad.campaigns?.name || 'No Campaign'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={statusOptions.find(s => s.value === ad.status)?.label || ad.status}
                                                            color={getStatusColor(ad.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {ad.impressions?.toLocaleString() || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {ad.clicks?.toLocaleString() || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatCTR(ad.ctr || 0)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {new Date(ad.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleActionMenuOpen(e, ad)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                </Paper>

                {/* Action Menu */}
                <Menu
                    anchorEl={actionMenuAnchor}
                    open={Boolean(actionMenuAnchor)}
                    onClose={handleActionMenuClose}
                >
                    <MenuItem onClick={() => {
                        handleViewAd(selectedAdForAction?.id);
                        handleActionMenuClose();
                    }}>
                        <ViewIcon sx={{ mr: 1 }} /> View Details
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleEditAd(selectedAdForAction);
                        handleActionMenuClose();
                    }}>
                        <EditIcon sx={{ mr: 1 }} /> Edit
                    </MenuItem>

                    {selectedAdForAction?.status !== 'active' && (
                        <MenuItem onClick={() => {
                            handleStatusChange(selectedAdForAction?.id, 'active');
                        }}>
                            <PlayIcon sx={{ mr: 1 }} /> Activate
                        </MenuItem>
                    )}

                    {selectedAdForAction?.status === 'active' && (
                        <MenuItem onClick={() => {
                            handleStatusChange(selectedAdForAction?.id, 'paused');
                        }}>
                            <PauseIcon sx={{ mr: 1 }} /> Pause
                        </MenuItem>
                    )}

                    {selectedAdForAction?.status !== 'archived' && (
                        <MenuItem onClick={() => {
                            handleStatusChange(selectedAdForAction?.id, 'archived');
                        }}>
                            <ArchiveIcon sx={{ mr: 1 }} /> Archive
                        </MenuItem>
                    )}

                    <MenuItem onClick={() => {
                        setDeleteDialogOpen(true);
                    }}>
                        <DeleteIcon sx={{ mr: 1 }} /> Delete
                    </MenuItem>
                </Menu>

                {/* Create/Edit Ad Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        {selectedAd ? 'Edit Ad' : 'Create New Ad'}
                    </DialogTitle>
                    <DialogContent>
                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                            <Tab label="Ad Details" />
                            <Tab label="Creative" />
                            <Tab label="Preview" />
                        </Tabs>

                        {tabValue === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="normal" required>
                                        <InputLabel>Campaign *</InputLabel>
                                        <Select
                                            value={formData.campaign_id}
                                            onChange={(e) => handleFormChange('campaign_id', e.target.value)}
                                            label="Campaign *"
                                        >
                                            <MenuItem value="">Select Campaign</MenuItem>
                                            {campaigns.map((campaign) => (
                                                <MenuItem key={campaign.id} value={campaign.id}>
                                                    {campaign.name} ({campaign.status})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        label="Ad Name *"
                                        value={formData.name}
                                        onChange={(e) => handleFormChange('name', e.target.value)}
                                        margin="normal"
                                        required
                                    />

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Ad Type</InputLabel>
                                        <Select
                                            value={formData.ad_type}
                                            onChange={(e) => handleFormChange('ad_type', e.target.value)}
                                            label="Ad Type"
                                        >
                                            {adTypeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.icon} {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Format</InputLabel>
                                        <Select
                                            value={formData.format}
                                            onChange={(e) => handleFormChange('format', e.target.value)}
                                            label="Format"
                                        >
                                            {formatOptions[formData.ad_type]?.map((format) => (
                                                <MenuItem key={format} value={format}>
                                                    {format}
                                                </MenuItem>
                                            )) || (
                                                    <MenuItem value={formData.format}>{formData.format}</MenuItem>
                                                )}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={formData.status}
                                            onChange={(e) => handleFormChange('status', e.target.value)}
                                            label="Status"
                                        >
                                            {statusOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Destination URL *"
                                        value={formData.destination_url}
                                        onChange={(e) => handleFormChange('destination_url', e.target.value)}
                                        margin="normal"
                                        required
                                        placeholder="https://example.com/landing-page"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Display URL"
                                        value={formData.display_url}
                                        onChange={(e) => handleFormChange('display_url', e.target.value)}
                                        margin="normal"
                                        placeholder="example.com"
                                        helperText="Shown in the ad, usually the domain"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Headline"
                                        value={formData.headline}
                                        onChange={(e) => handleFormChange('headline', e.target.value)}
                                        margin="normal"
                                        placeholder="Catchy headline for your ad"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => handleFormChange('description', e.target.value)}
                                        margin="normal"
                                        multiline
                                        rows={2}
                                        placeholder="Description text for your ad"
                                    />

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Call to Action</InputLabel>
                                        <Select
                                            value={formData.call_to_action}
                                            onChange={(e) => handleFormChange('call_to_action', e.target.value)}
                                            label="Call to Action"
                                        >
                                            {ctaOptions.map((cta) => (
                                                <MenuItem key={cta} value={cta}>
                                                    {cta}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}

                        {tabValue === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Creative URL"
                                        value={formData.creative_url}
                                        onChange={(e) => handleFormChange('creative_url', e.target.value)}
                                        margin="normal"
                                        placeholder="https://example.com/image.jpg or https://example.com/video.mp4"
                                        helperText="URL to your banner image, video, or other creative asset"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Thumbnail URL"
                                        value={formData.thumbnail_url}
                                        onChange={(e) => handleFormChange('thumbnail_url', e.target.value)}
                                        margin="normal"
                                        placeholder="https://example.com/thumbnail.jpg"
                                        helperText="Thumbnail image for video/audio ads"
                                    />

                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            For best results:
                                            <ul>
                                                <li>Use HTTPS URLs for all images and videos</li>
                                                <li>Ensure images are optimized for web (JPEG, PNG, WebP)</li>
                                                <li>Video ads should be in MP4 format with H.264 codec</li>
                                                <li>Keep file sizes reasonable for fast loading</li>
                                            </ul>
                                        </Typography>
                                    </Alert>
                                </Grid>
                            </Grid>
                        )}

                        {tabValue === 2 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    {renderAdPreview()}
                                </Grid>
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={selectedAd ? handleUpdateAd : handleCreateAd}
                            variant="contained"
                            disabled={!formData.name || !formData.campaign_id || !formData.destination_url}
                        >
                            {selectedAd ? 'Update Ad' : 'Create Ad'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Delete Ad</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete "{selectedAdForAction?.name}"? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteAd} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
};

export default AdsPage;